import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { Skeleton } from '../ui/Skeleton';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { DefaultParamsForm } from './default-params-form/DefaultParamsForm';
import {
  cloneDoc,
  defaultParamsDocToYaml,
  parseDefaultParamsYaml,
  type DefaultParamsDoc,
} from '../../lib/default-params-form-utils';

interface DefaultParamsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type EditorMode = 'form' | 'yaml';

export function DefaultParamsDialog({ open, onClose, onSuccess }: DefaultParamsDialogProps) {
  const { user } = useAuth();
  const { isAllowed: canManageByPermission } = usePermission('workflows', 'manage_default_params');
  const canManage = canManageByPermission || (user?.flags as any)?.is_admin === true;

  const [mode, setMode] = useState<EditorMode>('form');
  const [yamlContent, setYamlContent] = useState('');
  const [formDoc, setFormDoc] = useState<DefaultParamsDoc>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode('form');
      void loadYaml();
    }
  }, [open]);

  const loadYaml = async () => {
    setLoading(true);
    setError(null);
    setParseError(null);
    try {
      const response = await apiClient.getDefaultParams();
      const content = response?.yaml ?? '';
      setYamlContent(content);
      setFormDoc(parseDefaultParamsYaml(content || 'version: "3"\nworkflows: {}\ncli_commands: {}'));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load default params');
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setParseError(null);
      return true;
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;

    if (next === 'yaml') {
      try {
        setYamlContent(defaultParamsDocToYaml(formDoc));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (mode === 'yaml' && next === 'form') {
      try {
        setFormDoc(parseDefaultParamsYaml(yamlContent));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    setMode(next);
  };

  const handleSave = async () => {
    if (!canManage) return;

    let contentToSave = yamlContent;

    if (mode === 'form') {
      try {
        contentToSave = defaultParamsDocToYaml(formDoc);
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (!validateYaml(contentToSave)) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.updateDefaultParams(contentToSave);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save default params');
    } finally {
      setSaving(false);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    try {
      yaml.load(newValue);
      setParseError(null);
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <DialogTitle>Default Params</DialogTitle>
              <DialogDescription>
                {canManage
                  ? 'Workflow and CLI default config descriptor (`config/default-params.yaml`) — form for common fields, YAML for bulk edits.'
                  : 'View the descriptor. Only users with Manage Default Params or admins can edit.'}
              </DialogDescription>
            </div>
            {canManage ? (
              <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => switchMode('form')}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    mode === 'form'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  Form
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('yaml')}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    mode === 'yaml'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  YAML
                </button>
              </div>
            ) : null}
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {parseError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              {parseError}
            </div>
          )}

          {!loading && canManage && mode === 'form' && (
            <DefaultParamsForm doc={formDoc} onChange={(next) => setFormDoc(cloneDoc(next))} />
          )}

          {!loading && (!canManage || mode === 'yaml') && (
            <div className="relative">
              <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                <div className="max-h-[min(560px,55vh)] overflow-auto custom-scrollbar">
                  {canManage ? (
                    <Editor
                      value={yamlContent}
                      onValueChange={handleYamlChange}
                      highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                      padding={16}
                      style={{
                        fontFamily: '"Fira Code", "Fira Mono", monospace',
                        fontSize: 13,
                        backgroundColor: '#1e293b',
                        color: '#e2e8f0',
                        minHeight: 280,
                      }}
                      className="outline-none"
                    />
                  ) : (
                    <pre
                      className="p-4 m-0 font-mono text-sm text-gray-200"
                      style={{ minHeight: 280, backgroundColor: '#1e293b' }}
                    >
                      <code
                        dangerouslySetInnerHTML={{
                          __html: highlight(yamlContent, languages.yaml, 'yaml'),
                        }}
                      />
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Close
          </Button>
          {canManage && (
            <Button onClick={handleSave} disabled={saving || loading || !!parseError}>
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
